import { useState, useEffect, useMemo, Fragment } from 'react';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { Link } from 'react-router-dom';
import {
  getAllLessons,
  updateLessonStatus,
  resetLessonForCoach,
  retryVideoProcessing,
  transferLesson,
  transferOrder,
  getLessonHistory,
  getOrderHistory,
  getAllUsers,
  type LessonData,
  type AdminUserData,
} from '../../services/admin';
import LessonVideoPreview from '../../components/LessonVideoPreview';
import { LessonEditPreview } from '../../components/LessonEditPreview';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import type { LessonHistoryEvent, OfferingType, OrderHistoryEvent } from '../../types/lesson';
import { formatOfferingLabel } from '../../utils/offeringLabel';

// ── Types ────────────────────────────────────────────────────────────────────

interface BundleGroup {
  orderId: string;
  offeringTitle: string;
  coachId: string;
  coachName: string;
  playerId: string;
  playerName: string;
  totalLessons: number;
  lessons: LessonData[];
  isTransferred: boolean;
  isRefunded: boolean;
}

type TransferModalState =
  | { mode: 'lesson'; lesson: LessonData }
  | { mode: 'order'; orderId: string; label: string; coachId: string; coachName: string; pendingCount: number; deliveredCount: number };

type HistoryDrawerState =
  | { mode: 'lesson'; lesson: LessonData }
  | { mode: 'order'; orderId: string; label: string };

// ── Helpers ──────────────────────────────────────────────────────────────────

function statusColor(status: string) {
  const map: Record<string, string> = {
    reserved: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-purple-100 text-purple-800',
    delivered: 'bg-green-100 text-green-800',
  };
  return map[status] ?? 'bg-gray-100 text-gray-800';
}

function statusLabel(status: string) {
  const map: Record<string, string> = {
    reserved: 'Reserved',
    in_progress: 'In Progress',
    delivered: 'Delivered',
  };
  return map[status] ?? status;
}

function formatTs(ts: unknown) {
  if (!ts) return '-';
  const d = typeof ts === 'object' && ts !== null && 'toDate' in ts
    ? (ts as { toDate: () => Date }).toDate()
    : new Date(ts as string);
  return d.toLocaleString();
}

function coachDisplayName(c: AdminUserData) {
  return c.coachProfile?.displayName || c.displayName || c.email || c.uid;
}

/* A submission is "stalled" once it has been in 'submitting' longer than any
   real one takes. The upload that lost lesson 5DXMbk5z5K1YVvArNVVD ran 34
   seconds; 10 minutes is generous enough that a slow connection on a large
   draft will not trip it, and short enough to catch the same failure the day
   it happens rather than when a coach asks.

   Deliberately surfacing only. There is no retry button here: the existing
   "Retry Video" re-runs composition, which is a different job from finishing a
   submission, and wiring one to the other would quietly do the wrong thing. */
const STALLED_SUBMISSION_MS = 10 * 60 * 1000;

function submissionBadge(lesson: LessonData) {
  const s = lesson.submissionStatus;
  if (!s || s === 'delivered') return null;

  if (s === 'failed') {
    return (
      <span
        title={lesson.submissionError || undefined}
        className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-medium whitespace-nowrap"
      >
        Submission failed
      </span>
    );
  }

  const startedMs = lesson.submissionStartedAt ? Date.parse(lesson.submissionStartedAt) : NaN;
  const stalled = Number.isFinite(startedMs) && Date.now() - startedMs > STALLED_SUBMISSION_MS;
  return (
    <span
      title={lesson.submissionStartedAt ? `Started ${lesson.submissionStartedAt}` : undefined}
      className={`text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
        stalled ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
      }`}
    >
      {stalled ? 'Submission stalled' : 'Submitting…'}
    </span>
  );
}

function videoProcessingBadge(lesson: LessonData) {
  const s = lesson.videoProcessingStatus;
  if (!s || s === 'complete') return null;
  const map: Record<string, string> = {
    pending:    'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    failed:     'bg-red-100 text-red-800',
  };
  const labels: Record<string, string> = {
    pending:    'Video queued',
    processing: 'Video processing…',
    failed:     'Video failed',
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium mt-1 ${map[s]}`}>
      {labels[s]}
    </span>
  );
}

function bundleSummaryPill(group: BundleGroup) {
  const delivered = group.lessons.filter(l => l.status === 'delivered').length;
  const inProgress = group.lessons.filter(l => l.status === 'in_progress').length;
  const reserved = group.lessons.filter(l => l.status === 'reserved').length;
  const parts: string[] = [];
  if (delivered) parts.push(`${delivered} delivered`);
  if (inProgress) parts.push(`${inProgress} in progress`);
  if (reserved) parts.push(`${reserved} reserved`);
  return parts.join(' · ') || 'No lessons yet';
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AdminLessons() {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useToast();

  const [lessons, setLessons] = useState<LessonData[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Collapsible bundle rows
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  // Transfer modal
  const [transferModal, setTransferModal] = useState<TransferModalState | null>(null);
  const [coaches, setCoaches] = useState<AdminUserData[]>([]);
  const [coachesLoading, setCoachesLoading] = useState(false);
  const [transferCoachId, setTransferCoachId] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [transferPayOverride, setTransferPayOverride] = useState('');
  const [transferring, setTransferring] = useState(false);

  // History drawer
  const [historyDrawer, setHistoryDrawer] = useState<HistoryDrawerState | null>(null);
  const [lessonHistory, setLessonHistory] = useState<LessonHistoryEvent[]>([]);
  const [orderHistory, setOrderHistory] = useState<OrderHistoryEvent[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Video modal
  const [videoModal, setVideoModal] = useState<LessonData | null>(null);
  // Fresh download URLs resolved from storage paths when modal opens.
  // The URLs stored in Firestore are 7-day signed GCS URLs that expire;
  // getDownloadURL returns a non-expiring Firebase token URL instead.
  const [videoDownloadUrls, setVideoDownloadUrls] = useState<{ player?: string; coach?: string }>({});

  // Reset lesson confirm (stores lessonId pending confirmation)
  const [resetConfirmId, setResetConfirmId] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  // Retry video processing
  const [retryingVideoId, setRetryingVideoId] = useState<string | null>(null);

  useEffect(() => { loadLessons(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve fresh download URLs whenever the video modal opens
  useEffect(() => {
    if (!videoModal) { setVideoDownloadUrls({}); return; }
    let cancelled = false;
    (async () => {
      const urls: { player?: string; coach?: string } = {};
      if (videoModal.playerVideoStoragePath) {
        try { urls.player = await getDownloadURL(storageRef(storage, videoModal.playerVideoStoragePath)); }
        catch { urls.player = videoModal.playerVideoUrl; }
      } else {
        urls.player = videoModal.playerVideoUrl;
      }
      if (videoModal.coachVideoStoragePath) {
        try { urls.coach = await getDownloadURL(storageRef(storage, videoModal.coachVideoStoragePath)); }
        catch { urls.coach = videoModal.coachVideoUrl; }
      } else {
        urls.coach = videoModal.coachVideoUrl;
      }
      if (!cancelled) setVideoDownloadUrls(urls);
    })();
    return () => { cancelled = true; };
  }, [videoModal]);

  async function loadLessons() {
    try {
      setLoading(true);
      const data = await getAllLessons(statusFilter === 'all' ? undefined : statusFilter);
      setLessons(data);
    } catch {
      showError('Failed to load lessons');
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(lessonId: string, newStatus: string) {
    try {
      await updateLessonStatus(lessonId, newStatus as LessonData['status']);
      showSuccess('Lesson status updated');
      loadLessons();
    } catch {
      showError('Failed to update lesson status');
    }
  }

  // ── Grouping ────────────────────────────────────────────────────────────────

  const { bundles, singles } = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const filtered = lessons.filter(l =>
      !term ||
      l.coachName?.toLowerCase().includes(term) ||
      l.playerName?.toLowerCase().includes(term) ||
      l.offeringTitle?.toLowerCase().includes(term)
    );

    const bundleMap = new Map<string, LessonData[]>();
    const singles: LessonData[] = [];

    for (const lesson of filtered) {
      if (lesson.orderType === 'training_program' && lesson.orderId) {
        const arr = bundleMap.get(lesson.orderId) ?? [];
        arr.push(lesson);
        bundleMap.set(lesson.orderId, arr);
      } else {
        singles.push(lesson);
      }
    }

    const bundles: BundleGroup[] = [];
    for (const [orderId, groupLessons] of bundleMap) {
      const first = groupLessons[0];
      // Prefer order-level coach fields — these reflect post-transfer state even when all
      // existing lesson docs still carry the old coachId (e.g. only lesson 1 delivered so far).
      bundles.push({
        orderId,
        offeringTitle: first.offeringTitle ?? 'Training Program',
        coachId: first.orderCoachId ?? first.coachId,
        coachName: first.orderCoachName ?? first.coachName,
        playerId: first.playerId,
        playerName: first.playerName,
        totalLessons: first.totalLessons ?? groupLessons.length,
        lessons: [...groupLessons].sort((a, b) => (a.lessonNumber ?? 0) - (b.lessonNumber ?? 0)),
        isTransferred: groupLessons.some(l => l.isTransferred),
        isRefunded: groupLessons.some(l => l.orderRefunded),
      });
    }

    return { bundles, singles };
  }, [lessons, searchTerm]);

  // ── Transfer modal helpers ──────────────────────────────────────────────────

  async function ensureCoachesLoaded() {
    if (coaches.length > 0) return;
    setCoachesLoading(true);
    try {
      const all = await getAllUsers('coach');
      setCoaches(all.filter(c => c.isActive !== false));
    } catch {
      showError('Failed to load coaches');
    } finally {
      setCoachesLoading(false);
    }
  }

  async function openTransferForLesson(lesson: LessonData) {
    setTransferCoachId('');
    setTransferNote('');
    setTransferPayOverride('');
    setTransferModal({ mode: 'lesson', lesson });
    await ensureCoachesLoaded();
  }

  async function openTransferForOrder(group: BundleGroup) {
    setTransferCoachId('');
    setTransferNote('');
    setTransferPayOverride('');
    const deliveredCount = group.lessons.filter(l => l.status === 'delivered').length;
    // Use totalLessons so future unstarted lessons (not yet created as docs) are counted too.
    const pendingCount = group.totalLessons - deliveredCount;
    setTransferModal({
      mode: 'order',
      orderId: group.orderId,
      label: `${group.playerName} · ${group.offeringTitle}`,
      coachId: group.coachId,
      coachName: group.coachName,
      pendingCount,
      deliveredCount,
    });
    await ensureCoachesLoaded();
  }

  async function handleTransfer() {
    if (!transferModal || !transferCoachId || !currentUser) return;
    setTransferring(true);
    try {
      const adminName = currentUser.displayName ?? currentUser.email ?? undefined;
      const payOverrideCents = transferPayOverride
        ? Math.round(parseFloat(transferPayOverride) * 100)
        : undefined;

      if (transferModal.mode === 'lesson') {
        await transferLesson({
          lessonId: transferModal.lesson.id,
          newCoachId: transferCoachId,
          adminId: currentUser.uid,
          adminName,
          note: transferNote || undefined,
          payPerLessonCents: payOverrideCents,
        });
        showSuccess('Lesson transferred successfully');
      } else {
        await transferOrder({
          orderId: transferModal.orderId,
          newCoachId: transferCoachId,
          adminId: currentUser.uid,
          adminName,
          note: transferNote || undefined,
          payPerLessonCents: payOverrideCents,
        });
        showSuccess(`Training program transferred. ${transferModal.pendingCount} remaining lesson${transferModal.pendingCount !== 1 ? 's' : ''} reassigned`);
      }

      setTransferModal(null);
      loadLessons();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  }

  // ── History drawer ──────────────────────────────────────────────────────────

  async function openHistoryForLesson(lesson: LessonData) {
    setHistoryDrawer({ mode: 'lesson', lesson });
    setLessonHistory([]);
    setOrderHistory([]);
    setHistoryLoading(true);
    try {
      setLessonHistory(await getLessonHistory(lesson.id));
    } catch {
      showError('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  }

  async function openHistoryForOrder(group: BundleGroup) {
    setHistoryDrawer({ mode: 'order', orderId: group.orderId, label: `${group.playerName} · ${group.offeringTitle}` });
    setLessonHistory([]);
    setOrderHistory([]);
    setHistoryLoading(true);
    try {
      setOrderHistory(await getOrderHistory(group.orderId));
    } catch {
      showError('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  }

  // ── Retry video processing ──────────────────────────────────────────────────

  async function handleRetryVideo(lessonId: string) {
    setRetryingVideoId(lessonId);
    try {
      await retryVideoProcessing(lessonId);
      showSuccess('Video processing restarted. Check back in a few minutes');
      loadLessons();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setRetryingVideoId(null);
    }
  }

  // ── Reset lesson ────────────────────────────────────────────────────────────

  async function handleResetLesson(lessonId: string) {
    setResetting(true);
    try {
      await resetLessonForCoach(lessonId);
      showSuccess('Lesson reset to in progress');
      setResetConfirmId(null);
      loadLessons();
    } catch {
      showError('Failed to reset lesson');
    } finally {
      setResetting(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  const totalDisplayed = bundles.length + singles.length;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Lesson Management</h1>
        <p className="text-gray-600">View, update, and transfer lessons. Training programs are grouped by order.</p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col md:flex-row gap-4">
        <div className="flex gap-2 flex-wrap">
          {['all', 'reserved', 'in_progress', 'delivered'].map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-4 py-2 rounded-lg font-medium transition text-sm ${
                statusFilter === f
                  ? 'bg-tennis-green text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {f === 'all' ? `All (${lessons.length})` : statusLabel(f)}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search by coach, player, or title…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tennis-green text-sm input-glow"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Lesson / Program', 'Coach', 'Player', 'Status', 'Requested', 'Delivered', 'Actions'].map(h => (
                  <th
                    key={h}
                    className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      h === 'Actions' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">

              {/* Bundle groups */}
              {bundles.map(group => {
                const isExpanded = expandedOrders.has(group.orderId);
                // Show History if any lesson is transferred, OR if the order's current coach
                // differs from any lesson's coach (order transferred after all lessons delivered).
                const hasAnyTransfer = group.isTransferred || group.lessons.some(l => l.coachId !== group.coachId);
                const deliveredCount = group.lessons.filter(l => l.status === 'delivered').length;
                const isOrderComplete = deliveredCount >= group.totalLessons;
                const isRefunded = group.isRefunded;

                return (
                  <Fragment key={group.orderId}>
                    {/* Group header row */}
                    <tr
                      className="bg-blue-50 hover:bg-blue-100 cursor-pointer"
                      onClick={() => setExpandedOrders(prev => {
                        const next = new Set(prev);
                        if (next.has(group.orderId)) { next.delete(group.orderId); } else { next.add(group.orderId); }
                        return next;
                      })}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 text-xs">{isExpanded ? '▼' : '▶'}</span>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {group.offeringTitle}
                              <span className="ml-1.5 text-xs font-normal text-gray-500">
                                (Training Program · {group.lessons.length}/{group.totalLessons})
                              </span>
                            </div>
                            {hasAnyTransfer && (
                              <span className="inline-block mt-0.5 text-[10px] font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                                Transferred
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/coach/${group.coachId}`}
                          onClick={e => e.stopPropagation()}
                          className="text-sm text-tennis-green hover:text-tennis-ball no-underline"
                        >
                          {group.coachName}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/player/${group.playerId}`}
                          onClick={e => e.stopPropagation()}
                          className="text-sm text-tennis-green hover:text-tennis-ball no-underline"
                        >
                          {group.playerName}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-xs text-gray-600">{bundleSummaryPill(group)}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                        {formatTs(group.lessons[0]?.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                        {(() => {
                          const delivered = group.lessons.filter(l => l.deliveredAt);
                          const last = delivered[delivered.length - 1];
                          return last ? formatTs(last.deliveredAt) : '-';
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          {!isOrderComplete && !isRefunded && (
                            <button
                              data-testid="transfer-btn"
                              onClick={() => openTransferForOrder(group)}
                              className="text-xs px-2.5 py-1 rounded border border-amber-400 text-amber-700 hover:bg-amber-50 font-medium transition"
                            >
                              Transfer
                            </button>
                          )}
                          {hasAnyTransfer && (
                            <button
                              onClick={() => openHistoryForOrder(group)}
                              className="text-xs px-2.5 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium transition"
                            >
                              History
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Lesson sub-rows */}
                    {isExpanded && group.lessons.map(lesson => (
                      <tr key={lesson.id} className="bg-gray-50 border-l-4 border-blue-200">
                        <td className="pl-12 pr-6 py-3">
                          <div className="text-xs font-medium text-gray-700">
                            └ Lesson {lesson.lessonNumber ?? '?'}
                          </div>
                          {lesson.conversationId && (
                            <Link
                              to={`/admin/chats?conversation=${lesson.conversationId}`}
                              className="text-[11px] text-tennis-green hover:text-tennis-ball no-underline"
                            >
                              View Chat →
                            </Link>
                          )}
                        </td>
                        <td className="px-6 py-3 text-xs text-gray-400">-</td>
                        <td className="px-6 py-3 text-xs text-gray-400">-</td>
                        <td className="px-6 py-3">
                          <div className="flex flex-col items-start gap-0.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusColor(lesson.status)}`}>
                              {statusLabel(lesson.status)}
                            </span>
                            {videoProcessingBadge(lesson)}
                            {submissionBadge(lesson)}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-xs text-gray-500">{formatTs(lesson.createdAt)}</td>
                        <td className="px-6 py-3 text-xs text-gray-500">{formatTs(lesson.deliveredAt)}</td>
                        <td className="px-6 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <select
                              value={lesson.status}
                              onChange={e => handleStatusChange(lesson.id, e.target.value)}
                              className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-tennis-green input-glow"
                            >
                              <option value="reserved">Reserved</option>
                              <option value="in_progress">In Progress</option>
                              <option value="delivered">Delivered</option>
                            </select>
                            {(lesson.playerVideoUrl || lesson.publishedDraftId) && (
                              <button
                                onClick={() => setVideoModal(lesson)}
                                className="text-xs px-2.5 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 font-medium transition"
                              >
                                Videos
                              </button>
                            )}
                            {lesson.status === 'delivered' && (
                              resetConfirmId === lesson.id ? (
                                <span className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleResetLesson(lesson.id)}
                                    disabled={resetting}
                                    className="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 font-medium transition disabled:opacity-50"
                                  >
                                    {resetting ? '…' : 'Confirm'}
                                  </button>
                                  <button
                                    onClick={() => setResetConfirmId(null)}
                                    className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium transition"
                                  >
                                    Cancel
                                  </button>
                                </span>
                              ) : (
                                <button
                                  onClick={() => setResetConfirmId(lesson.id)}
                                  className="text-xs px-2.5 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 font-medium transition"
                                >
                                  Reset
                                </button>
                              )
                            )}
                            {lesson.isTransferred && (
                              <button
                                onClick={() => openHistoryForLesson(lesson)}
                                className="text-xs px-2.5 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium transition"
                              >
                                History
                              </button>
                            )}
                            {lesson.videoProcessingStatus === 'failed' && (
                              <button
                                onClick={() => handleRetryVideo(lesson.id)}
                                disabled={retryingVideoId === lesson.id}
                                className="text-xs px-2.5 py-1 rounded border border-orange-400 text-orange-700 hover:bg-orange-50 font-medium transition disabled:opacity-50"
                              >
                                {retryingVideoId === lesson.id ? '…' : 'Retry Video'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}

              {/* Single / shot-consult lessons */}
              {singles.map(lesson => (
                <tr key={lesson.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {lesson.offeringTitle || 'Lesson'}
                      {lesson.orderType === 'shot_consult' && (
                        <span className="ml-1.5 text-xs text-gray-400">(Shot Consult)</span>
                      )}
                    </div>
                    {lesson.isTransferred && (
                      <span className="inline-block mt-0.5 text-[10px] font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                        Transferred
                      </span>
                    )}
                    {lesson.conversationId && (
                      <Link
                        to={`/admin/chats?conversation=${lesson.conversationId}`}
                        className="block text-xs text-tennis-green hover:text-tennis-ball no-underline mt-0.5"
                      >
                        View Chat →
                      </Link>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link
                      to={`/coach/${lesson.coachId}`}
                      className="text-sm text-tennis-green hover:text-tennis-ball no-underline"
                    >
                      {lesson.coachName}
                    </Link>
                    {lesson.isTransferred && lesson.transferredFromCoachName && (
                      <div className="text-xs text-gray-400 mt-0.5">from {lesson.transferredFromCoachName}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link
                      to={`/player/${lesson.playerId}`}
                      className="text-sm text-tennis-green hover:text-tennis-ball no-underline"
                    >
                      {lesson.playerName}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col items-start gap-0.5">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor(lesson.status)}`}>
                        {statusLabel(lesson.status)}
                      </span>
                      {videoProcessingBadge(lesson)}
                      {submissionBadge(lesson)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">{formatTs(lesson.createdAt)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">{formatTs(lesson.deliveredAt)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <select
                        value={lesson.status}
                        onChange={e => handleStatusChange(lesson.id, e.target.value)}
                        className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-tennis-green input-glow"
                      >
                        <option value="reserved">Reserved</option>
                        <option value="in_progress">In Progress</option>
                        <option value="delivered">Delivered</option>
                      </select>
                      {(lesson.playerVideoUrl || lesson.publishedDraftId) && (
                        <button
                          onClick={() => setVideoModal(lesson)}
                          className="text-xs px-2.5 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 font-medium transition"
                        >
                          Videos
                        </button>
                      )}
                      {lesson.status === 'delivered' && (
                        resetConfirmId === lesson.id ? (
                          <span className="flex items-center gap-1">
                            <button
                              onClick={() => handleResetLesson(lesson.id)}
                              disabled={resetting}
                              className="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 font-medium transition disabled:opacity-50"
                            >
                              {resetting ? '…' : 'Confirm'}
                            </button>
                            <button
                              onClick={() => setResetConfirmId(null)}
                              className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium transition"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            onClick={() => setResetConfirmId(lesson.id)}
                            className="text-xs px-2.5 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 font-medium transition"
                          >
                            Reset
                          </button>
                        )
                      )}
                      {lesson.status !== 'delivered' && !lesson.orderRefunded && (
                        <button
                          data-testid="transfer-btn"
                          onClick={() => openTransferForLesson(lesson)}
                          className="text-xs px-2.5 py-1 rounded border border-amber-400 text-amber-700 hover:bg-amber-50 font-medium transition"
                        >
                          Transfer
                        </button>
                      )}
                      {lesson.isTransferred && (
                        <button
                          onClick={() => openHistoryForLesson(lesson)}
                          className="text-xs px-2.5 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium transition"
                        >
                          History
                        </button>
                      )}
                      {lesson.videoProcessingStatus === 'failed' && (
                        <button
                          onClick={() => handleRetryVideo(lesson.id)}
                          disabled={retryingVideoId === lesson.id}
                          className="text-xs px-2.5 py-1 rounded border border-orange-400 text-orange-700 hover:bg-orange-50 font-medium transition disabled:opacity-50"
                        >
                          {retryingVideoId === lesson.id ? '…' : 'Retry Video'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalDisplayed === 0 && (
          <div className="text-center py-12 text-gray-500">No lessons found</div>
        )}
      </div>

      {/* ── Transfer modal ──────────────────────────────────────────────────── */}
      {transferModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {transferModal.mode === 'order' ? 'Transfer Training Program' : 'Transfer Lesson to New Coach'}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {transferModal.mode === 'order' ? transferModal.label : (
                  <>
                    {transferModal.lesson.playerName} · {transferModal.lesson.offeringTitle || 'Lesson'}
                    {transferModal.lesson.lessonNumber != null && (
                      <> · Lesson {transferModal.lesson.lessonNumber}{transferModal.lesson.totalLessons != null && ` of ${transferModal.lesson.totalLessons}`}</>
                    )}
                  </>
                )}
              </p>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Order-level info box */}
              {transferModal.mode === 'order' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                  Transfers <strong>{transferModal.pendingCount} pending lesson{transferModal.pendingCount !== 1 ? 's' : ''}</strong> to the new coach.
                  {transferModal.deliveredCount > 0 && (
                    <> {transferModal.deliveredCount} already-delivered lesson{transferModal.deliveredCount !== 1 ? 's' : ''} will not be affected.</>
                  )}
                </div>
              )}

              {/* Current coach */}
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Current Coach</div>
                <div className="text-sm font-medium text-gray-900">
                  {transferModal.mode === 'order' ? transferModal.coachName : transferModal.lesson.coachName}
                </div>
              </div>

              {/* New coach selector */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  New Coach <span className="text-red-500">*</span>
                </label>
                {coachesLoading ? (
                  <div className="text-sm text-gray-400">Loading coaches…</div>
                ) : (
                  <select
                    value={transferCoachId}
                    onChange={e => setTransferCoachId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-tennis-green input-glow"
                  >
                    <option value="">Select a coach…</option>
                    {coaches
                      .filter(c => c.uid !== (transferModal.mode === 'order' ? transferModal.coachId : transferModal.lesson.coachId))
                      .map(c => (
                        <option key={c.uid} value={c.uid}>{coachDisplayName(c)}</option>
                      ))}
                  </select>
                )}
              </div>

              {/* Reason / note */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Reason (shown to incoming coach)
                </label>
                <textarea
                  value={transferNote}
                  onChange={e => setTransferNote(e.target.value)}
                  placeholder="e.g. Coach became unresponsive after 7 days"
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-tennis-green resize-none input-glow"
                />
              </div>

              {/* Pay override */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Pay per lesson override ($, leave blank to use offering default)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={transferPayOverride}
                  onChange={e => setTransferPayOverride(e.target.value)}
                  placeholder="e.g. 85"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-tennis-green input-glow"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setTransferModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-transfer-btn"
                onClick={handleTransfer}
                disabled={!transferCoachId || transferring}
                className="px-4 py-2 text-sm font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {transferring ? 'Transferring…' : 'Confirm Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Video modal ────────────────────────────────────────────────────── */}
      {videoModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Lesson Videos</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {videoModal.playerName} · {videoModal.offeringTitle || 'Lesson'}
                  {videoModal.lessonNumber != null && ` · Lesson ${videoModal.lessonNumber}`}
                </p>
              </div>
              <button onClick={() => setVideoModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="overflow-y-auto px-6 py-5 space-y-6">
              {/* Player video */}
              {videoModal.playerVideoUrl ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Player Video</h3>
                    {videoDownloadUrls.player ? (
                      <a
                        href={videoDownloadUrls.player}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Download
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400">Resolving…</span>
                    )}
                  </div>
                  <LessonVideoPreview
                    videoUrl={videoModal.playerVideoUrl}
                    maxHeight="300px"
                  />
                </div>
              ) : (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Player Video</h3>
                  <p className="text-sm text-gray-400 italic">No player video uploaded yet.</p>
                </div>
              )}

              {/* Coach response video */}
              {videoModal.publishedOriginalVideoId && videoModal.publishedDraftId ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Coach Response</h3>
                    {videoDownloadUrls.coach ? (
                      <a
                        href={videoDownloadUrls.coach}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Download
                      </a>
                    ) : videoModal.coachVideoUrl ? (
                      <span className="text-xs text-gray-400">Resolving…</span>
                    ) : null}
                  </div>
                  <LessonEditPreview
                    context={{ type: 'lesson', id: videoModal.id }}
                    originalVideoId={videoModal.publishedOriginalVideoId}
                    draftId={videoModal.publishedDraftId}
                    notes={videoModal.coachNotes}
                    coachName={videoModal.coachName}
                    offeringLabel={formatOfferingLabel({ offeringType: videoModal.orderType as OfferingType | undefined, lessonNumber: videoModal.lessonNumber, totalLessons: videoModal.totalLessons })}
                    deliveredAt={videoModal.deliveredAt}
                    showControls={true}
                    downloadUrl={videoDownloadUrls.coach}
                  />
                </div>
              ) : videoModal.coachNotes ? (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Coach Notes</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{videoModal.coachNotes}</p>
                </div>
              ) : null}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end flex-shrink-0">
              <button
                onClick={() => setVideoModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── History drawer ──────────────────────────────────────────────────── */}
      {historyDrawer && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Transfer History</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {historyDrawer.mode === 'order'
                    ? `${historyDrawer.label} · Order-level`
                    : `${historyDrawer.lesson.playerName} · ${historyDrawer.lesson.offeringTitle || 'Lesson'}`}
                </p>
              </div>
              <button onClick={() => setHistoryDrawer(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="overflow-y-auto px-6 py-4 space-y-4">
              {historyLoading && <p className="text-sm text-gray-400">Loading…</p>}

              {/* Lesson history */}
              {!historyLoading && historyDrawer.mode === 'lesson' && (
                lessonHistory.length === 0
                  ? <p className="text-sm text-gray-500">No transfer history.</p>
                  : lessonHistory.map((event, i) => (
                    <div key={event.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-bold flex-shrink-0">{i + 1}</div>
                        {i < lessonHistory.length - 1 && <div className="w-px flex-1 bg-gray-200 mt-1" />}
                      </div>
                      <div className="pb-4 flex-1">
                        <div className="text-xs text-gray-400 mb-0.5">
                          {new Date(event.createdAt).toLocaleString()}{event.adminName && <> · by {event.adminName}</>}
                        </div>
                        <div className="text-sm font-semibold text-gray-900">{event.fromCoachName} → {event.toCoachName}</div>
                        {event.lessonNumber != null && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            At lesson {event.lessonNumber}{event.totalLessons != null && ` of ${event.totalLessons}`}
                            {event.deliveredLessonsCount != null && ` · ${event.deliveredLessonsCount} already delivered`}
                          </div>
                        )}
                        {event.note && <div className="mt-1 text-xs italic text-gray-500">"{event.note}"</div>}
                        {event.payPerLessonCents != null && (
                          <div className="mt-0.5 text-xs text-gray-500">Pay override: ${(event.payPerLessonCents / 100).toFixed(2)}/lesson</div>
                        )}
                      </div>
                    </div>
                  ))
              )}

              {/* Order history */}
              {!historyLoading && historyDrawer.mode === 'order' && (
                orderHistory.length === 0
                  ? <p className="text-sm text-gray-500">No transfer history.</p>
                  : orderHistory.map((event, i) => (
                    <div key={event.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-bold flex-shrink-0">{i + 1}</div>
                        {i < orderHistory.length - 1 && <div className="w-px flex-1 bg-gray-200 mt-1" />}
                      </div>
                      <div className="pb-4 flex-1">
                        <div className="text-xs text-gray-400 mb-0.5">
                          {new Date(event.createdAt).toLocaleString()}{event.adminName && <> · by {event.adminName}</>}
                        </div>
                        <div className="text-sm font-semibold text-gray-900">{event.fromCoachName} → {event.toCoachName}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {event.lessonsTransferred} lesson{event.lessonsTransferred !== 1 ? 's' : ''} transferred
                          {event.deliveredLessonsCount > 0 && ` · ${event.deliveredLessonsCount} already delivered`}
                          {event.totalLessons != null && ` · ${event.totalLessons} total`}
                        </div>
                        {event.note && <div className="mt-1 text-xs italic text-gray-500">"{event.note}"</div>}
                        {event.payPerLessonCents != null && (
                          <div className="mt-0.5 text-xs text-gray-500">Pay override: ${(event.payPerLessonCents / 100).toFixed(2)}/lesson</div>
                        )}
                      </div>
                    </div>
                  ))
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setHistoryDrawer(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
